const API_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiClient {
  constructor() {
    this.baseUrl = API_URL;
  }

  getToken() {
    return localStorage.getItem('cs_token');
  }

  async request(path, options = {}) {
    const url = `${this.baseUrl}${path}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const res = await fetch(url, { ...options, headers });
    if (res.status === 204) return null;
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || data.detail || 'Request failed');
    return data;
  }

  get(path) { return this.request(path); }
  post(path, body) { return this.request(path, { method: 'POST', body: JSON.stringify(body) }); }
  put(path, body) { return this.request(path, { method: 'PUT', body: JSON.stringify(body) }); }
  del(path) { return this.request(path, { method: 'DELETE' }); }
}

const client = new ApiClient();

export const api = {
  // Generic methods (for direct API calls)
  get: (path) => client.get(path),
  post: (path, body) => client.post(path, body),
  put: (path, body) => client.put(path, body),
  delete: (path) => client.del(path),
  
  // Auth
  login: (email, password) => client.post('/api/auth/login/', { email, password }),
  register: (data) => client.post('/api/auth/register/', data),
  getMe: () => client.get('/api/auth/me/'),
  updateMe: (data) => client.put('/api/auth/me/', data),
  forgotPassword: (email) => client.post('/api/auth/forgot-password/', { email }),
  resetPassword: (token, password) => client.post('/api/auth/reset-password/', { token, password }),
  verifyResetToken: (token) => client.get(`/api/auth/verify-reset-token/?token=${encodeURIComponent(token)}`),
  googleAuth: (access_token) => client.post('/api/auth/google/', { access_token }),

  // Instances
  getInstances: (sync = false) => client.get(`/api/instances/${sync ? '?sync=true' : ''}`),
  getInstance: (id) => client.get(`/api/instances/${id}/`),
  createInstance: (data) => client.post('/api/instances/', data),
  updateInstance: (id, data) => client.put(`/api/instances/${id}/`, data),
  deleteInstance: (id) => client.del(`/api/instances/${id}/`),
  
  // Instance Actions
  startInstance: (id) => client.post(`/api/instances/${id}/start/`),
  stopInstance: (id) => client.post(`/api/instances/${id}/stop/`),
  restartInstance: (id) => client.post(`/api/instances/${id}/restart/`),
  getInstanceActions: (id) => client.get(`/api/instances/${id}/actions/`),
  
  // RDP Access
  getRDPCredentials: (id) => client.get(`/api/instances/${id}/rdp/`),
  downloadRDPFile: (id) => `${API_URL}/api/instances/${id}/rdp/download/`,
  resetInstancePassword: (id, password) => client.post(`/api/instances/${id}/reset-password/`, password ? { password } : {}),
  
  // Instance Metrics
  getInstanceMetrics: (id) => client.get(`/api/instances/${id}/metrics/`),
  
  // Backups
  getInstanceBackups: (id) => client.get(`/api/instances/${id}/backups/`),
  createInstanceBackup: (id, name) => client.post(`/api/instances/${id}/backups/`, { name }),
  restoreInstanceBackup: (id, backupId) => client.post(`/api/instances/${id}/backups/${backupId}/restore/`),
  
  // Snapshots
  getInstanceSnapshot: (id) => client.get(`/api/instances/${id}/snapshot/`),
  createInstanceSnapshot: (id) => client.post(`/api/instances/${id}/snapshot/`),
  deleteInstanceSnapshot: (id) => client.del(`/api/instances/${id}/snapshot/`),
  restoreInstanceSnapshot: (id) => client.post(`/api/instances/${id}/snapshot/restore/`),
  
  // Data Centers & Templates
  getDataCenters: () => client.get('/api/instances/data-centers/'),
  getOSTemplates: () => client.get('/api/instances/templates/'),

  // Skills
  getSkills: (params) => {
    const qs = new URLSearchParams(params || {}).toString();
    return client.get(`/api/skills/${qs ? '?' + qs : ''}`);
  },
  createSkill: (data) => client.post('/api/skills/', data),
  installSkill: (skillId, instanceId) => client.post(`/api/skills/${skillId}/install/`, { instance_id: instanceId }),

  // Blog
  getBlogPosts: (params) => {
    const qs = new URLSearchParams(params || {}).toString();
    return client.get(`/api/blog/${qs ? '?' + qs : ''}`);
  },
  getBlogPost: (slug) => client.get(`/api/blog/slug/${slug}/`),
  createBlogPost: (data) => client.post('/api/blog/', data),
  updateBlogPost: (id, data) => client.put(`/api/blog/${id}/`, data),
  deleteBlogPost: (id) => client.del(`/api/blog/${id}/`),

  // Billing
  getBillingOverview: () => client.get('/api/billing/overview/'),
  getInvoices: () => client.get('/api/billing/invoices/'),
  getCreditTransactions: () => client.get('/api/billing/credits/'),
  purchaseCredits: (amount, bonus) => client.post('/api/billing/credits/purchase/', { amount, bonus }),
  
  // Public Pricing
  getPricing: () => client.get('/api/billing/pricing/'),

  // Analytics
  getAnalytics: () => client.get('/api/analytics/'),
  getStatusServices: () => client.get('/api/analytics/status/'),
  getDailyMetrics: (days = 30) => client.get(`/api/analytics/metrics/daily/?days=${days}`),
  recordMetric: (type, cost, instanceId) => client.post('/api/analytics/metrics/record/', { type, cost, instance_id: instanceId }),

  // Support
  getTickets: () => client.get('/api/support/tickets/'),
  submitTicket: (data) => client.post('/api/support/tickets/', data),

  // ============= Notifications API =============
  getNotifications: (unreadOnly = false, limit = 50) => 
    client.get(`/api/notifications/?unread_only=${unreadOnly}&limit=${limit}`),
  getUnreadCount: () => client.get('/api/notifications/unread-count/'),
  markNotificationRead: (id) => client.post(`/api/notifications/${id}/read/`),
  markAllNotificationsRead: () => client.post('/api/notifications/mark-all-read/'),
  clearAllNotifications: () => client.del('/api/notifications/clear-all/'),
  testNotification: () => client.post('/api/notifications/test/'),

  // ============= Admin Only APIs =============
  // Platform Config (Superuser only)
  getConfigs: () => client.get('/api/analytics/admin/configs/'),
  updateConfig: (key, value) => client.put(`/api/analytics/admin/configs/${key}/`, { value }),
  createConfig: (data) => client.post('/api/analytics/admin/configs/create/', data),
  deleteConfig: (key) => client.del(`/api/analytics/admin/configs/delete/${key}/`),
  
  // Admin Pricing Management (Superuser only)
  getAdminPlans: () => client.get('/api/billing/admin/plans/'),
  createPlan: (data) => client.post('/api/billing/admin/plans/create/', data),
  updatePlan: (planId, data) => client.put(`/api/billing/admin/plans/${planId}/`, data),
  deletePlan: (planId) => client.del(`/api/billing/admin/plans/${planId}/delete/`),
  
  // Admin Free Trial Config (Superuser only)
  getTrialConfig: () => client.get('/api/billing/admin/trial/'),
  updateTrialConfig: (data) => client.put('/api/billing/admin/trial/update/', data),
  
  // Admin Stats (Superuser only)
  getAdminStats: () => client.get('/api/analytics/admin/stats/'),
  
  // Update Service Status (Superuser only)
  updateServiceStatus: (serviceName, data) => client.put(`/api/analytics/status/${serviceName}/`, data),

  // Static content (returned directly without backend call)
  getDocsSections: () => Promise.resolve([
    { title: 'Getting Started', items: ['Quick Start Guide', 'System Requirements', 'Creating Your First Instance', 'Connecting Messaging Apps'] },
    { title: 'Instance Management', items: ['Hardware Tiers', 'Region Selection', 'Start/Stop/Restart', 'Scaling Up', 'Migration'] },
    { title: 'OpenClaw Configuration', items: ['AI Model Setup', 'API Key Management', 'Skills Installation', 'Memory Configuration', 'Persona Setup'] },
    { title: 'Remote Access', items: ['RDP Connection Guide', 'SSH Access', 'Browser Terminal', 'File Transfer', 'IP Whitelisting'] },
    { title: 'Billing & Credits', items: ['Subscription Plans', 'Credit System', 'Auto-Recharge', 'Invoices', 'Cost Optimization'] },
    { title: 'API Reference', items: ['Authentication', 'Instances API', 'Billing API', 'Analytics API', 'Webhooks'] },
  ]),
  getTestimonials: () => Promise.resolve([
    { name: 'jonahships_', text: "Setup ClawStinger yesterday. All I have to say is, wow. One click and my OpenClaw was running. No SSH, no server config, just works." },
    { name: 'markjaquith', text: "ClawStinger feels like that 'just had to glue all the parts together' leap forward for OpenClaw hosting. Incredible experience." },
    { name: 'danpeguine', text: "Why ClawStinger is nuts: your agent runs 24/7, you get RDP access, pre-configured everything. No DevOps needed." },
    { name: 'nateliason', text: "Yeah this was 1,000% worth it. Managing multiple agents from one dashboard, autonomously running tasks. The future is here." },
    { name: 'davemorin', text: "At this point I don't even know what to call it. After a few weeks, this is the first time I've felt like living in the future." },
    { name: 'karpathy', text: "Excellent platform. Love the simplicity and the power of managed OpenClaw hosting." },
  ]),
};

export default api;
